interface ReceiverDetails {
    name: string;
    iban: string;
    edrpou: string;
    bankName: string;
    mfo: string;
}

interface GeneratedInvoice {
    number: string;
    timestamp: string;
    payerDetails: string;
}

interface BuildInvoicePdfDefinitionParams {
    t: (key: string, options?: Record<string, unknown>) => string;
    generatedInvoice: GeneratedInvoice;
    receiverDetails: ReceiverDetails;
    selectedPlanName: string;
    amount: string;
}

export const buildInvoicePdfDefinition = ({
    t,
    generatedInvoice,
    receiverDetails,
    selectedPlanName,
    amount,
}: BuildInvoicePdfDefinitionParams) => {
    const issuedDate = new Date(Number(generatedInvoice.timestamp)).toLocaleString();

    return {
        pageSize: 'A4',
        pageMargins: [36, 36, 36, 36],
        defaultStyle: {
            fontSize: 10,
        },
        content: [
            {
                columns: [
                    { text: t('invoicePdfTitle'), style: 'title' },
                    {
                        width: 'auto',
                        stack: [
                            { text: `${t('invoiceNumberLabel')}: ${generatedInvoice.number}`, alignment: 'right' },
                            { text: `${t('invoiceIssuedAtLabel')}: ${issuedDate}`, alignment: 'right' },
                        ],
                    },
                ],
            },
            { text: '\n' },
            {
                columns: [
                    {
                        width: '*',
                        stack: [
                            { text: t('receiverDetailsTitle'), style: 'sectionTitle' },
                            { text: `${t('receiverNameLabel')}: ${receiverDetails.name}` },
                            { text: `${t('receiverIbanLabel')}: ${receiverDetails.iban}` },
                            { text: `${t('receiverEdrpouLabel')}: ${receiverDetails.edrpou}` },
                            { text: `${t('receiverBankLabel')}: ${receiverDetails.bankName}` },
                            { text: `${t('receiverMfoLabel')}: ${receiverDetails.mfo}` },
                        ],
                    },
                    {
                        width: '*',
                        stack: [
                            { text: t('payerDetailsTitle'), style: 'sectionTitle' },
                            { text: generatedInvoice.payerDetails },
                        ],
                    },
                ],
                columnGap: 16,
            },
            { text: '\n' },
            {
                table: {
                    headerRows: 1,
                    widths: [24, '*', 40, 48, 54, 54],
                    body: [
                        [
                            { text: t('invoiceTableColumnNo'), style: 'tableHeader' },
                            { text: t('invoiceTableColumnName'), style: 'tableHeader' },
                            { text: t('invoiceTableColumnQty'), style: 'tableHeader', alignment: 'right' },
                            { text: t('invoiceTableColumnUnit'), style: 'tableHeader' },
                            { text: t('invoiceTableColumnPrice'), style: 'tableHeader', alignment: 'right' },
                            { text: t('invoiceTableColumnTotal'), style: 'tableHeader', alignment: 'right' },
                        ],
                        [
                            '1',
                            t('invoiceDescription', { plan: selectedPlanName }),
                            { text: '1', alignment: 'right' },
                            t('invoiceUnitService'),
                            { text: amount, alignment: 'right' },
                            { text: amount, alignment: 'right' },
                        ],
                    ],
                },
                layout: 'lightHorizontalLines',
            },
            { text: '\n' },
            {
                columns: [
                    { width: '*', text: `${t('invoiceNoVat')}` },
                    { width: 'auto', text: `${t('invoiceTotalLabel')}: ${amount}`, bold: true },
                ],
            },
            { text: '\n\n' },
            { text: t('invoiceGeneratedBySystem'), color: '#6c757d', fontSize: 9 },
        ],
        styles: {
            title: {
                fontSize: 18,
                bold: true,
            },
            sectionTitle: {
                fontSize: 11,
                bold: true,
                margin: [0, 0, 0, 6],
            },
            tableHeader: {
                bold: true,
                fillColor: '#f3f5f7',
            },
        },
    };
};
